import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Input, Button, Radio, Checkbox, Select, Rate, DatePicker, Spin,
  message, Result, Progress
} from 'antd';
import { CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import SurveyService from '../../services/SurveyService';
import {
  PublicShell, SurveyCard, Logo, SurveyTitle, SurveyDescription,
  QuestionBlock, SubmitBar, ThankYouCard
} from './styled';

const { TextArea } = Input;

const PublicSurveyPage = ({ previewMode = false, surveyData = null }) => {
  const { slug, id } = useParams();
  const [searchParams] = useSearchParams();
  const trackingId = searchParams.get('t');

  const [survey, setSurvey] = useState(surveyData);
  const [loading, setLoading] = useState(!surveyData);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [answers, setAnswers] = useState({});
  const [respondentInfo, setRespondentInfo] = useState({ name: '', email: '', phone: '' });
  const [accessCode, setAccessCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [startTime] = useState(Date.now());
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    if (surveyData) {
      setSurvey(surveyData);
      setLoading(false);
      return;
    }
    (async () => {
      const result = previewMode
        ? await SurveyService.getSurveyPreview(id)
        : await SurveyService.getPublicSurvey(slug);
      if (result.success) {
        setSurvey(result.data);
      } else {
        setError(result.error);
        setErrorStatus(result.status);
      }
      setLoading(false);
    })();
  }, [slug, id, previewMode, surveyData]);

  const setAnswer = (qid, value) => setAnswers((a) => ({ ...a, [qid]: value }));

  const handleSubmit = async () => {
    if (previewMode) {
      message.info('Preview mode — responses are not submitted');
      return;
    }
    // Validate required questions
    for (const q of survey.questions) {
      if (q.required) {
        const val = answers[q.id];
        const empty = val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
        if (empty) {
          message.warning(`Please answer: ${q.question}`);
          return;
        }
      }
    }

    if (survey.settings?.requiresCode && !accessCode) {
      message.warning('Access code required');
      return;
    }

    setSubmitting(true);
    const payload = {
      answers: Object.entries(answers).map(([questionId, answer]) => ({ questionId, answer })),
      respondentInfo,
      accessCode: survey.settings?.requiresCode ? accessCode : undefined,
      timeSpentSeconds: Math.round((Date.now() - startTime) / 1000),
      trackingId: trackingId || undefined
    };
    const result = await SurveyService.submitResponse(slug, payload);
    setSubmitting(false);

    if (result.success) {
      setSubmitted(result.data);
      // If a redirect URL was configured, redirect after a brief delay
      if (result.data.redirectUrl) {
        setTimeout(() => { window.location.href = result.data.redirectUrl; }, 2000);
      }
    } else {
      message.error(result.error);
    }
  };

  const branding = survey?.branding || {};
  const onePerPage = !!survey?.settings?.oneQuestionPerPage;
  const collectInfo = survey?.settings?.collectRespondentInfo !== false;

  // Apply (or skip) randomization once per survey load.
  const orderedQuestions = useMemo(() => {
    if (!survey) return [];
    const list = [...survey.questions];
    if (survey.settings?.randomizeQuestions) {
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
      }
    }
    return list;
  }, [survey]);

  // Build the page list when in one-question-per-page mode.
  // Pages: [optional access code] -> each question -> respondent info page.
  const pages = useMemo(() => {
    if (!survey) return [];
    const list = [];
    if (survey.settings?.requiresCode) list.push({ kind: 'code' });
    orderedQuestions.forEach((q, idx) => list.push({ kind: 'question', q, idx }));
    if (collectInfo) list.push({ kind: 'info' });
    return list;
  }, [survey, orderedQuestions]);

  const currentPage = onePerPage ? pages[pageIndex] : null;

  const isPageValid = () => {
    if (!currentPage) return true;
    if (currentPage.kind === 'code') return !!accessCode.trim();
    if (currentPage.kind === 'question' && currentPage.q.required) {
      const val = answers[currentPage.q.id];
      const empty = val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
      return !empty;
    }
    return true;
  };

  const goNext = () => {
    if (!isPageValid()) {
      message.warning('Please answer this question before continuing.');
      return;
    }
    setPageIndex((i) => Math.min(i + 1, pages.length - 1));
  };
  const goBack = () => setPageIndex((i) => Math.max(i - 1, 0));

  // Progress
  const answeredCount = useMemo(() => {
    if (!survey) return 0;
    return survey.questions.filter((q) => {
      const val = answers[q.id];
      return val !== undefined && val !== null && val !== '' && (!Array.isArray(val) || val.length > 0);
    }).length;
  }, [survey, answers]);

  const progressPercent = onePerPage && pages.length > 0
    ? Math.round(((pageIndex + 1) / pages.length) * 100)
    : (survey?.questions.length ? Math.round((answeredCount / survey.questions.length) * 100) : 0);

  if (loading) {
    return (
      <PublicShell>
        <div style={{ marginTop: 60 }}><Spin size="large" /></div>
      </PublicShell>
    );
  }

  if (error) {
    return (
      <PublicShell>
        <Result
          status={errorStatus === 404 ? '404' : '403'}
          title={errorStatus === 404 ? 'Survey Not Found' : 'Survey Unavailable'}
          subTitle={error}
        />
      </PublicShell>
    );
  }

  if (submitted) {
    return (
      <PublicShell $bg={branding.backgroundColor} $font={branding.fontFamily} $text={branding.textColor}>
        <ThankYouCard>
          <CheckCircleOutlined style={{ fontSize: 64, color: branding.primaryColor || '#52c41a' }} />
          <h2>{submitted.thankYouMessage || 'Thank you for your response!'}</h2>
          {submitted.thankYouImageUrl && (
            <img src={submitted.thankYouImageUrl} alt="" className="thank-you-image" />
          )}
          {submitted.redirectUrl && (
            <p style={{ color: '#8c8c8c', marginTop: 24 }}>Redirecting...</p>
          )}
        </ThankYouCard>
      </PublicShell>
    );
  }

  return (
    <PublicShell $preview={previewMode} $bg={branding.backgroundColor} $font={branding.fontFamily} $text={branding.textColor}>
      {previewMode && (
        <div style={{
          background: '#fffbe6',
          border: '1px solid #ffe58f',
          color: '#874d00',
          padding: '8px 16px',
          borderRadius: 8,
          margin: '0 auto 16px',
          maxWidth: 720,
          textAlign: 'center',
          fontWeight: 500
        }}>
          Preview mode — status: {survey.status?.toUpperCase() || 'DRAFT'}. Responses will not be saved.
        </div>
      )}
      <SurveyCard $preview={previewMode}>
        {branding.logoUrl && <Logo src={branding.logoUrl} alt="Logo" />}
        <SurveyTitle $color={branding.textColor}>{survey.title}</SurveyTitle>
        {survey.description && <SurveyDescription>{survey.description}</SurveyDescription>}

        {branding.showProgressBar && survey.questions.length > 0 && (
          <Progress
            percent={progressPercent}
            strokeColor={branding.primaryColor || '#E31B23'}
            style={{ marginBottom: 24 }}
          />
        )}

        {!onePerPage && (
          <>
            {survey.settings?.requiresCode && (
              <QuestionBlock>
                <div className="question-label">
                  <LockOutlined /> Access Code <span className="required">*</span>
                </div>
                <Input
                  placeholder="Enter access code"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  size="large"
                />
              </QuestionBlock>
            )}

            {orderedQuestions.map((q, idx) => (
              <QuestionBlock key={q.id}>
                <div className="question-label">
                  {survey.settings?.showQuestionNumbers && `${idx + 1}. `}
                  {q.question}
                  {q.required && <span className="required">*</span>}
                </div>
                {q.description && <div className="description">{q.description}</div>}
                <QuestionInput
                  question={q}
                  value={answers[q.id]}
                  onChange={(v) => setAnswer(q.id, v)}
                  primaryColor={branding.primaryColor}
                />
              </QuestionBlock>
            ))}

            {/* Optional respondent info */}
            {collectInfo && (
            <QuestionBlock>
              <div className="question-label">Your Information (Optional)</div>
              <div className="description">All fields are optional. Leave blank to respond anonymously.</div>
              <Input
                placeholder="Name"
                value={respondentInfo.name}
                onChange={(e) => setRespondentInfo({ ...respondentInfo, name: e.target.value })}
                style={{ marginBottom: 8 }}
              />
              <Input
                placeholder="Email"
                type="email"
                value={respondentInfo.email}
                onChange={(e) => setRespondentInfo({ ...respondentInfo, email: e.target.value })}
                style={{ marginBottom: 8 }}
              />
              <Input
                placeholder="Phone"
                value={respondentInfo.phone}
                onChange={(e) => setRespondentInfo({ ...respondentInfo, phone: e.target.value })}
              />
            </QuestionBlock>
            )}

            <SubmitBar>
              <Button
                type="primary"
                size="large"
                loading={submitting}
                onClick={handleSubmit}
                style={{
                  background: branding.buttonColor || '#E31B23',
                  borderColor: branding.buttonColor || '#E31B23',
                  color: branding.buttonTextColor || '#fff',
                  minWidth: 160
                }}
              >
                Submit Response
              </Button>
            </SubmitBar>
          </>
        )}

        {onePerPage && currentPage && (
          <>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12, textAlign: 'right' }}>
              Step {pageIndex + 1} of {pages.length}
            </div>

            {currentPage.kind === 'code' && (
              <QuestionBlock>
                <div className="question-label">
                  <LockOutlined /> Access Code <span className="required">*</span>
                </div>
                <Input
                  placeholder="Enter access code"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  size="large"
                />
              </QuestionBlock>
            )}

            {currentPage.kind === 'question' && (
              <QuestionBlock>
                <div className="question-label">
                  {survey.settings?.showQuestionNumbers && `${currentPage.idx + 1}. `}
                  {currentPage.q.question}
                  {currentPage.q.required && <span className="required">*</span>}
                </div>
                {currentPage.q.description && <div className="description">{currentPage.q.description}</div>}
                <QuestionInput
                  question={currentPage.q}
                  value={answers[currentPage.q.id]}
                  onChange={(v) => setAnswer(currentPage.q.id, v)}
                  primaryColor={branding.primaryColor}
                />
              </QuestionBlock>
            )}

            {currentPage.kind === 'info' && (
              <QuestionBlock>
                <div className="question-label">Your Information (Optional)</div>
                <div className="description">All fields are optional. Leave blank to respond anonymously.</div>
                <Input
                  placeholder="Name"
                  value={respondentInfo.name}
                  onChange={(e) => setRespondentInfo({ ...respondentInfo, name: e.target.value })}
                  style={{ marginBottom: 8 }}
                />
                <Input
                  placeholder="Email"
                  type="email"
                  value={respondentInfo.email}
                  onChange={(e) => setRespondentInfo({ ...respondentInfo, email: e.target.value })}
                  style={{ marginBottom: 8 }}
                />
                <Input
                  placeholder="Phone"
                  value={respondentInfo.phone}
                  onChange={(e) => setRespondentInfo({ ...respondentInfo, phone: e.target.value })}
                />
              </QuestionBlock>
            )}

            <SubmitBar style={{ justifyContent: 'space-between', display: 'flex' }}>
              <Button
                size="large"
                onClick={goBack}
                disabled={pageIndex === 0}
              >
                Back
              </Button>
              {pageIndex < pages.length - 1 ? (
                <Button
                  type="primary"
                  size="large"
                  onClick={goNext}
                  style={{
                    background: branding.buttonColor || '#E31B23',
                    borderColor: branding.buttonColor || '#E31B23',
                    color: branding.buttonTextColor || '#fff',
                    minWidth: 120
                  }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  loading={submitting}
                  onClick={handleSubmit}
                  style={{
                    background: branding.buttonColor || '#E31B23',
                    borderColor: branding.buttonColor || '#E31B23',
                    color: branding.buttonTextColor || '#fff',
                    minWidth: 160
                  }}
                >
                  Submit Response
                </Button>
              )}
            </SubmitBar>
          </>
        )}
      </SurveyCard>
    </PublicShell>
  );
};

const QuestionInput = ({ question, value, onChange, primaryColor }) => {
  switch (question.type) {
    case 'short_text':
      return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} size="large" />;
    case 'long_text':
      return <TextArea rows={4} value={value || ''} onChange={(e) => onChange(e.target.value)} />;
    case 'email':
      return <Input type="email" value={value || ''} onChange={(e) => onChange(e.target.value)} size="large" />;
    case 'phone':
      return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} size="large" />;
    case 'number':
      return <Input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value)} size="large" />;
    case 'date':
      return (
        <DatePicker
          style={{ width: '100%' }}
          value={value ? dayjs(value) : null}
          onChange={(d) => onChange(d ? d.toISOString() : null)}
          size="large"
        />
      );
    case 'single_choice':
      return (
        <Radio.Group value={value} onChange={(e) => onChange(e.target.value)} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(question.options || []).map((opt) => (
            <Radio key={opt.id} value={opt.value || opt.label}>{opt.label}</Radio>
          ))}
        </Radio.Group>
      );
    case 'multiple_choice':
      return (
        <Checkbox.Group
          value={value || []}
          onChange={onChange}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {(question.options || []).map((opt) => (
            <Checkbox key={opt.id} value={opt.value || opt.label}>{opt.label}</Checkbox>
          ))}
        </Checkbox.Group>
      );
    case 'dropdown':
      return (
        <Select
          value={value}
          onChange={onChange}
          style={{ width: '100%' }}
          size="large"
          placeholder="Select an option"
          options={(question.options || []).map((opt) => ({ value: opt.value || opt.label, label: opt.label }))}
        />
      );
    case 'yes_no':
      return (
        <Radio.Group value={value} onChange={(e) => onChange(e.target.value)}>
          <Radio.Button value="yes">Yes</Radio.Button>
          <Radio.Button value="no">No</Radio.Button>
        </Radio.Group>
      );
    case 'rating':
      return <Rate value={value || 0} onChange={onChange} style={{ color: primaryColor || '#E31B23' }} />;
    case 'scale': {
      const min = question.scaleMin ?? 0;
      const max = question.scaleMax ?? 10;
      const range = [];
      for (let i = min; i <= max; i++) range.push(i);
      return (
        <div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {range.map((n) => (
              <Button
                key={n}
                onClick={() => onChange(n)}
                style={value === n ? {
                  background: primaryColor || '#E31B23',
                  borderColor: primaryColor || '#E31B23',
                  color: '#fff',
                  minWidth: 40
                } : { minWidth: 40 }}
              >
                {n}
              </Button>
            ))}
          </div>
          {(question.scaleMinLabel || question.scaleMaxLabel) && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              <span>{question.scaleMinLabel}</span>
              <span>{question.scaleMaxLabel}</span>
            </div>
          )}
        </div>
      );
    }
    default:
      return <Input value={value || ''} onChange={(e) => onChange(e.target.value)} />;
  }
};

export default PublicSurveyPage;
